import { Component, h, JSX, Element, Prop, State, Watch } from '@stencil/core';
import debounce from 'debounce';
import { StringService } from '../../services';
import { responsive, getElementSizes } from '../../style';
import { HTMLRcmImageElementExtended, Ratio, ratioKeys, ratioValues, RcmImageProp } from './rcm-image.types';

@Component({
  tag: 'rcm-image',
  styleUrl: 'rcm-image.scss',
  shadow: true,
})
export class RcmImage {
  /**
   * Image URL or ImageProp. Regardless, the `src` (as string or inside the ImageProp):
   * - supports "$0" (width) and "$1" (height)
   * - these values are replaced with the suitable image width and height for the component size
   */
  @Prop() public src!: string | RcmImageProp;

  /** Image caption - overrides any `caption` in the `src` prop if it is an ImageProp */
  @Prop() public caption: string;

  /** Image copyright text - overrides any `copyright` in the `src` prop if it is an ImageProp */
  @Prop() public copyright: string;

  /** Image alt text - overrides any `alt` in the `src` prop if it is an ImageProp */
  @Prop() public alt: string;

  /** Image ratio */
  @Prop() public ratio: Ratio = Ratio.HD;

  @Prop() overrideMaxWidth: number = null;

  /** Full Container Height */
  @Prop() public fullHeight = false;

  /** Ignore its container and make the image occupy the entire width */
  @Prop() public fullscreen = false;

  /** Manage if title/ caption should be displayed */
  @Prop() public showCaption = false;

  /** Manage if copyright should be displayed */
  @Prop() public showCopyright = false;

  @Prop() public backgroundImageOnly: boolean;

  @Prop() public disableMarginTop: boolean;

  @Prop() includeMarginTop: boolean = false;

  /** Width of responsive image - **For internal use only** */
  @State() componentSize: number;

  @State() private imageWidth?: number;

  @State() private componentHeight?: number;

  @Element() private element: HTMLRcmImageElementExtended;

  private sizes: number[];

  private get imageAttributes(): RcmImageProp {
    const parsed = StringService.stringToObject(this.src) as string | RcmImageProp;

    const maxW = this.overrideMaxWidth || 4000;

    const src = typeof parsed === 'string' ? parsed : (parsed || {}).src;
    // Use Storyblok image resizing service to use focus point, max width 4000px
    const originalWidth = src?.split('/f/')[1]?.split('/')[1]?.split('x')[0] || '';
    const cleanWidth = parseInt(originalWidth, 10) <= maxW ? originalWidth : `${maxW}`;
    const [width, height] = this.ratioParsed.split(':').map(parseFloat) || [1, 1];
    let replacedSrc =
      this.ratio !== 'original'
        ? (src || '')
            .replace(/\$0/g, `${Math.floor(parseInt(cleanWidth, 10))}`)
            .replace(/\$1/g, `${Math.floor((parseInt(cleanWidth, 10) * height) / width).toString()}`)
        : (src || '').replace(/\/\$0x\$1/g, '');
    replacedSrc = replacedSrc.indexOf('focal(null)') > -1 ? replacedSrc.replace('filters:focal(null)', '') : replacedSrc;

    if (replacedSrc.indexOf('/m/') === -1) {
      // check if it ends with '/m'
      replacedSrc = replacedSrc.match(/\/m$/) ? `${replacedSrc}/` : `${replacedSrc}/m/`;
    }

    return typeof parsed === 'string'
      ? {
          src: replacedSrc,
          caption: this.caption ? this.caption : '',
          copyright: this.copyright,
          alt: this.alt,
        }
      : {
          src: replacedSrc,
          caption: this.caption || parsed?.caption || '',
          copyright: this.copyright || parsed?.copyright,
          alt: this.alt || parsed?.alt,
        };
  }

  private get ratioParsed(): Ratio {
    const index = ratioValues.indexOf(this.ratio);
    return index >= 0 ? Ratio[ratioKeys[index]] : Ratio.ORIGINAL;
  }

  private get imageHeight(): string {
    if (this.ratioParsed === Ratio.ORIGINAL) {
      return 'auto';
    }
    const [width, height] = this.ratioParsed.split(':').map(parseFloat);
    return width && height ? Math.floor((this.imageWidth * height) / width).toString() : '0';
  }

  private imageElement: HTMLDivElement;

  @Watch('componentSize')
  private updateSize(): void {
    const index = this.sizes.findIndex((size) => size === this.componentSize);
    this.imageWidth = this.sizes[index < 0 ? this.sizes.length - 1 : Math.max(0, index - 1)];
    this.updateHeight();
  }

  @Watch('ratio')
  private updateHeight(): void {
    if (!this.imageElement) {
      return;
    }
    const { width: elementWidth } = this.imageElement.getBoundingClientRect();
    const [width, height] = this.ratioParsed.split(':').map(parseFloat);

    this.componentHeight = width && height ? Math.floor((elementWidth * height) / width) : 0;
  }

  public componentWillLoad(): void {
    // Expose the image height so that other components can overlay anything correctly on top
    Object.defineProperty(this.element, 'imageElementHeight', {
      get: function getter(): number {
        return this.imageElement.getBoundingClientRect().height;
      }.bind(this),
    });

    this.sizes = getElementSizes(this.element);
    this.updateSize();
    responsive(this.element, 'component-size', this.imageElement, debounce(this.updateHeight.bind(this), 50));
  }

  public componentDidLoad(): void {
    // this.sizes = getElementSizes(this.element);
    // this.updateSize();
    // responsive(
    //   this.element,
    //   'component-size',
    //   this.imageElement,
    //   debounce(this.updateHeight.bind(this), 50),
    // );
  }

  public render(): JSX.Element {
    const { src, caption, copyright, alt } = this.imageAttributes || {};

    if (!StringService.isValidImageSrc(src)) return null;

    const imageClasses = {
      image: true,
      'image--fullscreen': this.fullscreen,
      'image--full-height': this.fullHeight,
      'image--original': this.ratioParsed === Ratio.ORIGINAL,
    };
    const imageContainerClasses = {
      'image-container': true,
      'image-container--full-height': this.fullHeight,
      'margin-top': !this.disableMarginTop && this.includeMarginTop,
    };
    const imageStyle = {
      backgroundImage: `url('${src}')`,
      ...(!!this.componentHeight &&
        !this.fullHeight && {
          height: `${this.componentHeight}px`,
        }),
    };

    return (
      <figure class={imageContainerClasses}>
        <div
          class={imageClasses}
          style={imageStyle}
          ref={(element) => {
            this.imageElement = element;
          }}
        >
          {this.backgroundImageOnly ? '' : <img src={src} alt={alt} title={caption} loading="lazy" />}
        </div>
        {((this.showCaption && caption) || (this.showCopyright && copyright)) && (
          <div class="description">
            {this.showCaption && caption && <figcaption class="caption">{caption}</figcaption>}
            {this.showCopyright && copyright && <div class="copyright">&copy; {copyright}</div>}
          </div>
        )}
      </figure>
    );
  }
}
